/**
 * Chat Page
 * Lists all conversations
 */

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { chatAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  MessageCircle,
  Search,
} from 'lucide-react';
import { format, isToday, isYesterday } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

function ChatPage() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    loadConversations();
  }, []);

  const loadConversations = async () => {
    try {
      const response = await chatAPI.getConversations();
      // API returns { success: true, data: [...] }
      setConversations(response.data || response.conversations || []);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isToday(date)) {
      return format(date, 'h:mm a');
    } else if (isYesterday(date)) {
      return 'Yesterday';
    } else {
      return format(date, 'MMM d');
    }
  };

  const getOtherParticipant = (conversation) => {
    // In a two-person conversation, get the other person's info
    if (conversation.other_user) {
      return conversation.other_user;
    }
    // Fallback
    return {
      first_name: 'User',
      last_name: '',
    };
  };

  const filteredConversations = conversations.filter(conv => {
    if (!searchQuery) return true;
    const other = getOtherParticipant(conv);
    const name = `${other.first_name} ${other.last_name}`.toLowerCase();
    return name.includes(searchQuery.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          Messages
        </h1>
        <p className="text-navy-600 mt-1">
          Your conversations with yacht owners and helpers
        </p>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search conversations..."
          className="input-field pl-10"
        />
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
      </div>

      {/* Conversations List */}
      {filteredConversations.length === 0 ? (
        <div className="card text-center py-12">
          <MessageCircle className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-navy-900 mb-2">
            {searchQuery ? 'No conversations found' : 'No messages yet'}
          </h3>
          <p className="text-navy-600">
            {searchQuery
              ? 'Try a different search term'
              : 'Start a conversation by contacting a helper or yacht owner'}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-luxury divide-y divide-gray-100">
          {filteredConversations.map((conversation) => {
            const other = getOtherParticipant(conversation);
            const hasUnread = conversation.unread_count > 0;

            return (
              <Link
                key={conversation.id}
                to={`/messages/${conversation.id}`}
                className="flex items-center p-4 hover:bg-gray-50 transition-colors"
              >
                {/* Avatar */}
                <div className={`h-12 w-12 rounded-full flex items-center justify-center flex-shrink-0 ${
                  hasUnread ? 'bg-primary-100' : 'bg-gray-100'
                }`}>
                  {other.profile_photo_url ? (
                    <img
                      src={other.profile_photo_url.startsWith('http') ? other.profile_photo_url : `${UPLOADS_BASE_URL}${other.profile_photo_url}`}
                      alt=""
                      className="h-12 w-12 rounded-full object-cover"
                    />
                  ) : (
                    <span className={`font-medium ${hasUnread ? 'text-primary-700' : 'text-navy-600'}`}>
                      {other.first_name?.[0]}{other.last_name?.[0]}
                    </span>
                  )}
                </div>

                {/* Content */}
                <div className="ml-4 flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className={`font-medium truncate ${hasUnread ? 'text-navy-900' : 'text-navy-700'}`}>
                      {other.first_name} {other.last_name}
                    </p>
                    <span className="text-xs text-navy-400 flex-shrink-0 ml-2">
                      {formatTime(conversation.last_message_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between mt-1">
                    <p className={`text-sm truncate ${hasUnread ? 'text-navy-700 font-medium' : 'text-navy-500'}`}>
                      {conversation.last_message || 'No messages yet'}
                    </p>
                    {hasUnread && (
                      <span className="ml-2 h-5 w-5 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center flex-shrink-0">
                        {conversation.unread_count > 9 ? '9+' : conversation.unread_count}
                      </span>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default ChatPage;
